import * as React from "react";
import { TrashIcon } from "@heroicons/react/outline";
import Title from "../../../../../common/title/title";
import CoverPhoto from "./cover_photo";
import AddPhoto from "./add_photo";
import { DroneDTO } from "../../../../../store/drone_slice";
import { compressImages } from "../../../../../utils/image-utils";

interface PhotoData {
  id: string;
  file: File;
  url: string;
}

interface StepProps {
  droneData: Omit<DroneDTO, "id" | "postedBy"> & {
    imageKeys: string[];
  };
  updateDroneData: (newData: Partial<Omit<DroneDTO, "id" | "postedBy">>) => void;
}

const DronePhotos: React.FC<StepProps> = ({ droneData, updateDroneData }) => {
  const [coverPhoto, setCoverPhoto] = React.useState<PhotoData | null>(
    droneData.imageKeys[0] ? {
      id: 'cover',
      url: droneData.imageKeys[0],
      file: droneData.imageFiles?.[0] || new File([], "placeholder")
    } : null
  );
  
  const [additionalPhotos, setAdditionalPhotos] = React.useState<PhotoData[]>(
    droneData.imageKeys.slice(1).map((url, index) => ({
      id: `${Date.now()}-${index}`,
      file: droneData.imageFiles?.[index + 1] || new File([], "placeholder"),
      url: url,
    })),
  );
  
  const [isCompressing, setIsCompressing] = React.useState(false);

  const updateImages = (cover: PhotoData | null, additional: PhotoData[]) => {
    const allPhotoData = cover ? [cover, ...additional] : [...additional];

    // Filter to keep only S3 keys of existing images (exclude blob: URLs)
    const existingImageKeys = allPhotoData
      .map((p) => p.url)
      .filter((url) => url && !url.startsWith('blob:'));

    // Filter to include only new File objects corresponding to blob: preview URLs
    const newImageFiles = allPhotoData
      .filter((p) => p.url && p.url.startsWith('blob:'))
      .map((p) => p.file);

    updateDroneData({
      imageKeys: existingImageKeys,
      imageFiles: newImageFiles,
    });
  };

  const handleCoverPhotoUpload = async (files: File[]) => {
    if (!files || files.length === 0) return;
    
    setIsCompressing(true);
    try {
      const compressedFiles = await compressImages(files);
      const [first, ...rest] = compressedFiles;
    if (!first) return;
      
    const newCoverPhoto = {
      id: 'cover',
      file: first,
      url: URL.createObjectURL(first)
    };
      
    // Only add up to 9 additional photos (since cover is separate)
    const remainingSlots = 9 - additionalPhotos.length;
    const filesToAdd = rest.slice(0, remainingSlots);
    const newPhotos: PhotoData[] = filesToAdd.map((file) => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      file: file,
      url: URL.createObjectURL(file),
    }));
      
    const updatedPhotos = [...additionalPhotos, ...newPhotos];
    setCoverPhoto(newCoverPhoto);
    setAdditionalPhotos(updatedPhotos);
    updateImages(newCoverPhoto, updatedPhotos);
    } catch (error) {
      console.error('Error compressing images:', error);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleCoverPhotoDelete = () => {
    setCoverPhoto(null);
    updateImages(null, additionalPhotos);
  };

  const handleAdditionalPhotoUpload = async (files: File[]) => {
    const remainingSlots = 9 - additionalPhotos.length;
    const filesToAdd = files.slice(0, remainingSlots);
    
    setIsCompressing(true);
    try {
      const compressedFiles = await compressImages(filesToAdd);
      const newPhotos: PhotoData[] = compressedFiles.map((file) => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      file: file,
      url: URL.createObjectURL(file),
    }));
      
    const updatedPhotos = [...additionalPhotos, ...newPhotos];
    setAdditionalPhotos(updatedPhotos);
    updateImages(coverPhoto, updatedPhotos);
    } catch (error) {
      console.error('Error compressing images:', error);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleAdditionalPhotoDelete = (id: string) => {
    const updatedPhotos = additionalPhotos.filter((photo) => photo.id !== id);
    setAdditionalPhotos(updatedPhotos);
    updateImages(coverPhoto, updatedPhotos);
  };

  return (
    <div className="w-full max-w-4xl mx-auto mt-8 px-4">
      <Title
        title="Add up to 10 photos to showcase your work"
        subtitle="People react to images so make sure to use your best shots of the product"
      />
      <div className="mt-4 space-y-4">
        <CoverPhoto
          onUpload={handleCoverPhotoUpload}
          onDelete={handleCoverPhotoDelete}
          coverPhoto={coverPhoto?.url || null}
        />
        {coverPhoto && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {additionalPhotos.map((photo) => (
              <div key={photo.id} className="relative w-full aspect-[5/3]">
                <img
                  src={photo.url}
                  alt={"Drone photo"}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  onClick={() => handleAdditionalPhotoDelete(photo.id)}
                  className="absolute top-2 right-2 bg-white p-1 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <TrashIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            ))}
            {additionalPhotos.length < 9 && (
              <AddPhoto onUpload={handleAdditionalPhotoUpload} />
            )}
          </div>
        )}
      </div>
      
      {isCompressing && (
        <div className="mt-4 text-center text-blue-600">
          <p>Compressing images...</p>
        </div>
      )}
    </div>
  );
};

export default DronePhotos;
